import type {Dispatch, ReactNode, SetStateAction} from 'react';
// @ts-expect-error
import type {Control, UseFormGetValues, UseFormSetValue, Watch} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {DragOverlay} from '@dnd-kit/core';

import type {FormPageFragment} from 'nutshell-graphql-types';

import {getFormSchema} from '../../../forms-selectors';

import type {FormSchemaProperty} from '../../../../core/nutshell-fields/types';

import {FORM_EDITOR_SORTABLE_OVERLAY_MODIFIERS} from '../drag-and-drop/consts';
import {formEditorDndLayoutStyles} from '../drag-and-drop/form-editor-dnd-layout';
import {FormEditorDragOverlayContent} from '../drag-and-drop/form-editor-drag-overlay-content';
import type {PageFieldArrayOps} from '../drag-and-drop/types';
import type {
    EditCustomFieldSession,
    NewCustomFieldFormPayload,
} from '../sidebar/add-custom-field/types';
import type {
    CustomFieldView,
    DraggableCardData,
} from '../sidebar/blocks-and-design/content-blocks/types';
import type {SidebarState} from '../sidebar/sidebar';
import type {MultiPageForm} from '../types';

import {FormEditor} from './form-editor';

interface Props {
    formId: string;
    formHeader?: ReactNode;
    dndPageFieldMutatorsRef: React.MutableRefObject<Map<number, PageFieldArrayOps>>;
    control: Control;
    watch: Watch;
    getValues: UseFormGetValues<MultiPageForm>;
    fieldCreateAtIndex: {pageIndex: number; insertIndex: number} | null;
    saveFailedErrorMessage: string | null | undefined;
    onClearError: () => void;
    minimumOneEmailField?: boolean;
    sidebarDragCard: DraggableCardData | null;
    reorderingFieldKey: string | null;
    graphqlPages?: FormPageFragment[] | null;
    sidebarState: SidebarState;
    setSidebarState: (state: SidebarState) => void;
    customFieldView: CustomFieldView | null;
    editCustomFieldSession: EditCustomFieldSession | null;
    setEditCustomFieldSession: Dispatch<SetStateAction<EditCustomFieldSession | null>>;
    openEditCustomFieldSession: (session: EditCustomFieldSession) => void;
    setValue: UseFormSetValue<MultiPageForm>;
    onPickExistingSchemaFieldForForm: (field: FormSchemaProperty) => void;
    onSubmitNewField: (payload: NewCustomFieldFormPayload) => Promise<void>;
}

export function FormEditorMainContent(props: Props) {
    const backgroundColor = props.watch('backgroundColor');
    const fontStyle = props.watch('fontStyle');
    const sortableOverlayFontFamily = fontStyle?.fontFamily;
    const formSchema = useSelector(getFormSchema);

    const dragOverlayModifiers = props.reorderingFieldKey
        ? FORM_EDITOR_SORTABLE_OVERLAY_MODIFIERS
        : undefined;

    return (
        <>
            <div style={{backgroundColor}} className='pos-relative full-height'>
                <FormEditor
                    formId={props.formId}
                    formHeader={props.formHeader}
                    dndPageFieldMutatorsRef={props.dndPageFieldMutatorsRef}
                    control={props.control}
                    watch={props.watch}
                    getValues={props.getValues}
                    fieldCreateAtIndex={props.fieldCreateAtIndex}
                    saveFailedErrorMessage={props.saveFailedErrorMessage}
                    onClearError={props.onClearError}
                    minimumOneEmailField={props.minimumOneEmailField}
                    graphqlPages={props.graphqlPages}
                    sidebarState={props.sidebarState}
                    setSidebarState={props.setSidebarState}
                    customFieldView={props.customFieldView}
                    editCustomFieldSession={props.editCustomFieldSession}
                    setEditCustomFieldSession={props.setEditCustomFieldSession}
                    openEditCustomFieldSession={props.openEditCustomFieldSession}
                    setValue={props.setValue}
                    onPickExistingSchemaFieldForForm={props.onPickExistingSchemaFieldForForm}
                    onSubmitNewField={props.onSubmitNewField}
                />
            </div>
            <DragOverlay
                dropAnimation={null}
                className={formEditorDndLayoutStyles['cursor-grabbing']}
                modifiers={dragOverlayModifiers}
            >
                <FormEditorDragOverlayContent
                    sidebarDragCard={props.sidebarDragCard}
                    reorderingFieldKey={props.reorderingFieldKey}
                    sortableOverlayFontFamily={sortableOverlayFontFamily}
                    control={props.control}
                    watch={props.watch}
                    formSchema={formSchema}
                />
            </DragOverlay>
        </>
    );
}
